import { ArrowRight, Clock, Cake, Coffee, Gift } from "lucide-react";
import AnimatedSection from "@/components/AnimatedSection";
import PropertyContact from "@/components/PropertyContact";
import { CONTACTS } from "@/data/contacts";

const IMAGES = {
  hero: "/images/stock/bakery_interior.jpg",
  pastry: "/images/stock/bakery_pastry.jpg",
  cake: "/images/stock/bakery_cake.jpg",
};

const SPECIALTIES = [
  { name: "Handmade Cakes", desc: "Birthday cakes, wedding cakes, custom designs — all baked fresh and made to order." },
  { name: "Patisserie", desc: "Croissants, pastries, and baked treats. Everything is made in-house, nothing outsourced." },
  { name: "Bread & Buns", desc: "Freshly baked every morning. Soft buns, sandwich bread, and traditional varieties." },
  { name: "Cookies & Biscuits", desc: "Butter cookies, rusks, and snack biscuits — the kind you stock up on every visit." },
  { name: "Savoury Bakes", desc: "Puffs, rolls, and savoury breads — ready to go, baked throughout the day." },
  { name: "Custom & Bulk Orders", desc: "We do B2B as well. Events, corporate orders, and regular supply — just ask." },
];

const FEATURES = [
  { icon: Clock, label: "Baked Fresh Daily" },
  { icon: Cake, label: "Custom Cakes" },
  { icon: Coffee, label: "Ready-Made & Made-to-Order" },
  { icon: Gift, label: "B2B & B2C" },
];

export default function AmrutBakeryPage() {
  return (
    <div data-testid="amrut-bakery-page">
      {/* ─── HERO ─── */}
      <section className="relative h-[80vh] md:h-screen w-full overflow-hidden">
        <img src={IMAGES.hero} alt="Amrut Delight" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-charcoal/85 via-charcoal/45 to-charcoal/40 pointer-events-none" />
        <div className="relative z-10 flex flex-col justify-end h-full p-8 md:p-16 lg:p-24 pb-20 md:pb-28">
          <AnimatedSection delay={0.2}><p className="overline-text text-ivory/70 mb-4">Artisan Bakery</p></AnimatedSection>
          <AnimatedSection delay={0.4}><h1 className="font-serif text-5xl md:text-6xl lg:text-8xl text-ivory font-light tracking-tighter leading-none mb-6 drop-shadow-lg">Amrut Delight</h1></AnimatedSection>
          <AnimatedSection delay={0.6}><p className="text-sm md:text-base text-ivory/85 max-w-lg leading-relaxed">Handmade cakes, fresh patisserie, and in-house baking. Two outlets in Karwar, serving both retail and wholesale.</p></AnimatedSection>
        </div>
      </section>

      {/* ─── INTRO ─── */}
      <section className="py-16 md:py-24 px-6 md:px-10 lg:px-16">
        <div className="max-w-[1200px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          <AnimatedSection>
            <div className="gold-divider mb-8" />
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-charcoal font-light tracking-tight leading-tight mb-8">Everything baked<br />in-house</h2>
            <p className="text-base text-charcoal/60 leading-relaxed mb-6">
              Amrut Delight has been running for about 7-8 years now, with two outlets in Karwar —
              one opposite the Karwar Bus Depot and another opposite Amrut Restaurant.
              Everything is manufactured in our own kitchen. Nothing is outsourced.
            </p>
            <p className="text-base text-charcoal/60 leading-relaxed">
              We serve both individual customers and businesses. Whether you need a cake for
              a birthday or a regular supply for your shop, we handle both. Handmade cakes
              are what people come to us for, and the patisserie is something we take real pride in.
            </p>
          </AnimatedSection>
          <AnimatedSection delay={0.2} className="overflow-hidden h-[50vh] md:h-[60vh]">
            <img src={IMAGES.pastry} alt="Fresh baked goods" className="w-full h-full object-cover" />
          </AnimatedSection>
        </div>
      </section>

      {/* ─── SPECIALTIES ─── */}
      <section data-testid="bakery-specialties" className="py-16 md:py-24 px-6 md:px-10 lg:px-16 bg-ivory">
        <div className="max-w-[1200px] mx-auto">
          <AnimatedSection><p className="overline-text text-charcoal/50 mb-6">What We Bake</p><h2 className="font-serif text-4xl md:text-5xl text-charcoal font-light tracking-tight mb-20">Fresh, Every Day</h2></AnimatedSection>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-0">
            {SPECIALTIES.map((item, i) => (
              <AnimatedSection key={i} delay={i * 0.08}>
                <div className="border-t border-charcoal/15 py-8 md:py-10">
                  <h4 className="font-serif text-xl md:text-2xl text-charcoal font-light tracking-tight mb-3">{item.name}</h4>
                  <p className="text-sm text-charcoal/60 leading-relaxed">{item.desc}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* ─── FEATURES ─── */}
      <section className="py-16 md:py-24 px-6 md:px-10 lg:px-16">
        <div className="max-w-[1200px] mx-auto">
          <AnimatedSection><p className="overline-text text-charcoal/40 mb-6">Why Amrut Delight</p><div className="gold-divider mb-16" /></AnimatedSection>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-12 md:gap-10">
            {FEATURES.map((f, i) => (
              <AnimatedSection key={i} delay={i * 0.1}>
                <div className="text-center"><f.icon size={28} strokeWidth={1} className="text-charcoal/40 mx-auto mb-4" /><p className="text-xs text-charcoal/60 tracking-wide">{f.label}</p></div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* ─── OUTLETS ─── */}
      <section className="py-10 md:py-14 px-6 md:px-10 lg:px-16 bg-ivory">
        <div className="max-w-[900px] mx-auto text-center">
          <AnimatedSection>
            <p className="overline-text text-charcoal/50 mb-6">Two Outlets in Karwar</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-8">
              <div className="border-t border-charcoal/15 pt-6">
                <h4 className="font-serif text-xl text-charcoal font-light mb-2">Outlet 1</h4>
                <p className="text-sm text-charcoal/55">Opposite Karwar Bus Depot</p>
              </div>
              <div className="border-t border-charcoal/15 pt-6">
                <h4 className="font-serif text-xl text-charcoal font-light mb-2">Outlet 2</h4>
                <p className="text-sm text-charcoal/55">Opposite Amrut Restaurant</p>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* ─── CONTACT ─── */}
      <section className="py-12 md:py-16 px-6 md:px-10 lg:px-16">
        <div className="max-w-[1200px] mx-auto">
          <AnimatedSection><PropertyContact contact={CONTACTS.bakery} variant="light" /></AnimatedSection>
        </div>
      </section>

      {/* ─── CTA ─── */}
      <section className="py-10 md:py-14 px-6 md:px-10 lg:px-16 bg-ivory text-center">
        <div className="max-w-[700px] mx-auto">
          <AnimatedSection><h2 className="font-serif text-4xl md:text-5xl text-charcoal font-light tracking-tight mb-6">Order Fresh</h2></AnimatedSection>
          <AnimatedSection delay={0.1}><p className="text-sm text-charcoal/55 leading-relaxed mb-10 max-w-md mx-auto">For custom cakes, bulk orders, or just to know what's fresh today — reach out.</p></AnimatedSection>
          <AnimatedSection delay={0.2}>
            <a href={`https://wa.me/${CONTACTS.bakery.whatsappRaw}?text=I'd%20like%20to%20order%20from%20Amrut%20Bakery`} target="_blank" rel="noopener noreferrer" data-testid="bakery-order-btn" className="inline-flex items-center gap-3 px-10 py-4 border border-charcoal/25 text-charcoal text-xs tracking-[0.2em] uppercase font-medium hover:bg-charcoal hover:text-ivory transition-all duration-500">
              Order via WhatsApp <ArrowRight size={14} strokeWidth={1.5} />
            </a>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
}
